// Shared between frontend and backend
import type { ColorGroup, ColorToken } from "@/components/DesignTokens/types";
import type { ComboBoxOptionType } from "@/components/RightStyle/TailwindStylingPanel/components/input";
import PROPERTIES from "@/lib/tailwind/properties";

// Shared between frontend and backend
export interface ClassType {
  tailwind: string;
  css: string;
}

export interface ClassTypeWithPropertyType extends ClassType {
  propertyType: PropertyType;
  isDesignToken?: boolean;
}

export enum PropertyType {
  WIDTH = "WIDTH",
  MIN_WIDTH = "MIN_WIDTH",
  MAX_WIDTH = "MAX_WIDTH",
  HEIGHT = "HEIGHT",
  MIN_HEIGHT = "MIN_HEIGHT",
  MAX_HEIGHT = "MAX_HEIGHT",
  ROTATE = "ROTATE",
  BORDER_RADIUS = "BORDER_RADIUS",
  BORDER_STYLE = "BORDER_STYLE",
  BORDER_COLOR = "BORDER_COLOR",
  BORDER_WIDTH = "BORDER_WIDTH",
  BORDER_TOP_COLOR = "BORDER_TOP_COLOR",
  BORDER_TOP_WIDTH = "BORDER_TOP_WIDTH",
  BORDER_BOTTOM_COLOR = "BORDER_BOTTOM_COLOR",
  BORDER_BOTTOM_WIDTH = "BORDER_BOTTOM_WIDTH",
  BORDER_LEFT_COLOR = "BORDER_LEFT_COLOR",
  BORDER_LEFT_WIDTH = "BORDER_LEFT_WIDTH",
  BORDER_RIGHT_COLOR = "BORDER_RIGHT_COLOR",
  BORDER_RIGHT_WIDTH = "BORDER_RIGHT_WIDTH",
  BORDER_TOP_LEFT_RADIUS = "BORDER_TOP_LEFT_RADIUS",
  BORDER_TOP_RIGHT_RADIUS = "BORDER_TOP_RIGHT_RADIUS",
  BORDER_BOTTOM_RIGHT_RADIUS = "BORDER_BOTTOM_RIGHT_RADIUS",
  BORDER_BOTTOM_LEFT_RADIUS = "BORDER_BOTTOM_LEFT_RADIUS",
  FONT_WEIGHT = "FONT_WEIGHT",
  FONT_SIZE = "FONT_SIZE",
  LINE_HEIGHT = "LINE_HEIGHT",
  LETTER_SPACING = "LETTER_SPACING",
  PADDING = "PADDING",
  PADDING_VERTICAL = "PADDING_VERTICAL",
  PADDING_HORIZONTAL = "PADDING_HORIZONTAL",
  PADDING_TOP = "PADDING_TOP",
  PADDING_BOTTOM = "PADDING_BOTTOM",
  PADDING_LEFT = "PADDING_LEFT",
  PADDING_RIGHT = "PADDING_RIGHT",
  MARGIN = "MARGIN",
  MARGIN_VERTICAL = "MARGIN_VERTICAL",
  MARGIN_HORIZONTAL = "MARGIN_HORIZONTAL",
  MARGIN_TOP = "MARGIN_TOP",
  MARGIN_BOTTOM = "MARGIN_BOTTOM",
  MARGIN_LEFT = "MARGIN_LEFT",
  MARGIN_RIGHT = "MARGIN_RIGHT",
  COLUMN_GAP = "COLUMN_GAP",
  ROW_GAP = "ROW_GAP",
  ANIMATION = "ANIMATION",
  BLUR = "BLUR",
  BOX_SHADOW = "BOX_SHADOW",
  OPACITY = "OPACITY",
  TEXT_COLOR = "TEXT_COLOR",
  BACKGROUND_COLOR = "BACKGROUND_COLOR",
  JUSTIFY_CONTENT = "JUSTIFY_CONTENT",
  ALIGN_ITEMS = "ALIGN_ITEMS",
  ALIGN_CONTENT = "ALIGN_CONTENT",
  FONT_FAMILY = "FONT_FAMILY",
  FONT_STYLE = "FONT_STYLE",
  TEXT_DECORATION_LINE = "TEXT_DECORATION_LINE",
  LEFT = "LEFT",
  TOP = "TOP",
  RIGHT = "RIGHT",
  BOTTOM = "BOTTOM",
  POSITION = "POSITION",
  BACKGROUND_IMAGE = "BACKGROUND_IMAGE",
  FROM_COLOR = "FROM_COLOR",
  TO_COLOR = "TO_COLOR",
  VIA_COLOR = "VIA_COLOR",
  FROM_POSITION = "FROM_POSITION",
  TO_POSITION = "TO_POSITION",
  VIA_POSITION = "VIA_POSITION",
  TEXT_ALIGN = "TEXT_ALIGN",
  DISPLAY = "DISPLAY",
  FLEX_DIRECTION = "FLEX_DIRECTION",
  FLEX_WRAP = "FLEX_WRAP",
  FLEX_GROW = "FLEX_GROW",
  FLEX_SHRINK = "FLEX_SHRINK",
}

export const tailwindFilterFunctions: {
  [key in PropertyType]: (prop: ClassType) => boolean;
} = {
  [PropertyType.WIDTH]: (prop) =>
    prop.css.startsWith("width:") || prop.tailwind.startsWith("w-"),
  [PropertyType.MIN_WIDTH]: (prop) =>
    prop.css.startsWith("min-width:") || prop.tailwind.startsWith("min-w-"),
  [PropertyType.MAX_WIDTH]: (prop) =>
    prop.css.startsWith("max-width:") || prop.tailwind.startsWith("max-w-"),
  [PropertyType.HEIGHT]: (prop) =>
    prop.css.startsWith("height:") || prop.tailwind.startsWith("h-"),
  [PropertyType.MIN_HEIGHT]: (prop) =>
    prop.css.startsWith("min-height:") || prop.tailwind.startsWith("min-h-"),
  [PropertyType.MAX_HEIGHT]: (prop) =>
    prop.css.startsWith("max-height:") || prop.tailwind.startsWith("max-h-"),
  [PropertyType.ROTATE]: (prop) => prop.tailwind.startsWith("rotate-"),
  [PropertyType.BORDER_RADIUS]: (prop) =>
    prop.css.startsWith("border-radius:") ||
    prop.tailwind.startsWith("rounded-"),
  [PropertyType.BORDER_TOP_LEFT_RADIUS]: (prop) =>
    prop.tailwind.startsWith("rounded-tl-"),
  [PropertyType.BORDER_TOP_RIGHT_RADIUS]: (prop) =>
    prop.tailwind.startsWith("rounded-tr-"),
  [PropertyType.BORDER_BOTTOM_RIGHT_RADIUS]: (prop) =>
    prop.tailwind.startsWith("rounded-br-"),
  [PropertyType.BORDER_BOTTOM_LEFT_RADIUS]: (prop) =>
    prop.tailwind.startsWith("rounded-bl-"),
  [PropertyType.BORDER_STYLE]: (prop) =>
    prop.css.startsWith("border-style:") && prop.tailwind.startsWith("border-"),
  [PropertyType.BORDER_COLOR]: (prop) =>
    prop.css.startsWith("border-color:") &&
    prop.tailwind.startsWith("border-") &&
    !prop.tailwind.includes("/"), // Filter only opacity variants
  [PropertyType.BORDER_WIDTH]: (prop) =>
    prop.css.startsWith("border-width:") && prop.tailwind.startsWith("border"),
  [PropertyType.BORDER_TOP_COLOR]: (prop) =>
    prop.css.startsWith("border-top-color:") &&
    prop.tailwind.startsWith("border-t-") &&
    !prop.tailwind.includes("/"),
  [PropertyType.BORDER_TOP_WIDTH]: (prop) =>
    prop.css.startsWith("border-top-width:") &&
    prop.tailwind.startsWith("border-t"),
  [PropertyType.BORDER_LEFT_COLOR]: (prop) =>
    prop.css.startsWith("border-left-color:") &&
    prop.tailwind.startsWith("border-l-") &&
    !prop.tailwind.includes("/"),
  [PropertyType.BORDER_LEFT_WIDTH]: (prop) =>
    prop.css.startsWith("border-left-width:") &&
    prop.tailwind.startsWith("border-l"),
  [PropertyType.BORDER_RIGHT_COLOR]: (prop) =>
    prop.css.startsWith("border-right-color:") &&
    prop.tailwind.startsWith("border-r-") &&
    !prop.tailwind.includes("/"),
  [PropertyType.BORDER_RIGHT_WIDTH]: (prop) =>
    prop.css.startsWith("border-right-width:") &&
    prop.tailwind.startsWith("border-r"),
  [PropertyType.BORDER_BOTTOM_COLOR]: (prop) =>
    prop.css.startsWith("border-bottom-color:") &&
    prop.tailwind.startsWith("border-b-") &&
    !prop.tailwind.includes("/"),
  [PropertyType.BORDER_BOTTOM_WIDTH]: (prop) =>
    prop.css.startsWith("border-bottom-width:") &&
    prop.tailwind.startsWith("border-b"),
  [PropertyType.FONT_WEIGHT]: (prop) => prop.css.startsWith("font-weight:"),
  [PropertyType.FONT_FAMILY]: (prop) => prop.css.startsWith("font-family:"),
  [PropertyType.FONT_STYLE]: (prop) => prop.css.startsWith("font-style:"),
  [PropertyType.TEXT_DECORATION_LINE]: (prop) =>
    ["underline", "overline", "line-through", "no-underline"].includes(
      prop.tailwind,
    ),
  [PropertyType.FONT_SIZE]: (prop) => prop.css.startsWith("font-size:"),
  [PropertyType.LINE_HEIGHT]: (prop) =>
    prop.css.startsWith("line-height:") || prop.tailwind.startsWith("leading-"),
  [PropertyType.LETTER_SPACING]: (prop) =>
    prop.css.startsWith("letter-spacing:") ||
    prop.tailwind.startsWith("tracking-"),
  [PropertyType.PADDING]: (prop) =>
    prop.css.startsWith("padding:") || prop.tailwind.startsWith("p-"),
  [PropertyType.PADDING_VERTICAL]: (prop) => prop.tailwind.startsWith("py-"),
  [PropertyType.PADDING_HORIZONTAL]: (prop) => prop.tailwind.startsWith("px-"),
  [PropertyType.PADDING_TOP]: (prop) => prop.tailwind.startsWith("pt-"),
  [PropertyType.PADDING_BOTTOM]: (prop) => prop.tailwind.startsWith("pb-"),
  [PropertyType.PADDING_LEFT]: (prop) => prop.tailwind.startsWith("pl-"),
  [PropertyType.PADDING_RIGHT]: (prop) => prop.tailwind.startsWith("pr-"),
  [PropertyType.MARGIN]: (prop) =>
    prop.css.startsWith("margin:") || prop.tailwind.startsWith("m-"),
  [PropertyType.MARGIN_VERTICAL]: (prop) => prop.tailwind.startsWith("my-"),
  [PropertyType.MARGIN_HORIZONTAL]: (prop) => prop.tailwind.startsWith("mx-"),
  [PropertyType.MARGIN_TOP]: (prop) => prop.tailwind.startsWith("mt-"),
  [PropertyType.MARGIN_BOTTOM]: (prop) => prop.tailwind.startsWith("mb-"),
  [PropertyType.MARGIN_LEFT]: (prop) => prop.tailwind.startsWith("ml-"),
  [PropertyType.MARGIN_RIGHT]: (prop) => prop.tailwind.startsWith("mr-"),
  [PropertyType.COLUMN_GAP]: (prop) =>
    prop.css.startsWith("column-gap:") || prop.tailwind.startsWith("gap-x-"),
  [PropertyType.ROW_GAP]: (prop) =>
    prop.css.startsWith("row-gap:") || prop.tailwind.startsWith("gap-y-"),
  [PropertyType.ANIMATION]: (prop) => prop.tailwind.startsWith("animate-"),
  [PropertyType.BLUR]: (prop) => prop.tailwind.startsWith("blur"),
  [PropertyType.OPACITY]: (props) =>
    props.css.startsWith("opacity:") || props.tailwind.startsWith("opacity-"),
  [PropertyType.BACKGROUND_COLOR]: (prop) => {
    // More inclusive filter for debugging
    const hasBackgroundCss = prop.css.includes("background-color");
    const isBgClass = prop.tailwind.startsWith("bg-");
    const hasOpacity = prop.tailwind.includes("/");

    return hasBackgroundCss && isBgClass && !hasOpacity;
  },
  [PropertyType.JUSTIFY_CONTENT]: (prop) =>
    prop.css.startsWith("justify-content:"),

  [PropertyType.ALIGN_ITEMS]: (prop) => prop.css.startsWith("align-items:"),

  [PropertyType.ALIGN_CONTENT]: (prop) => prop.css.startsWith("align-content:"),

  [PropertyType.BOX_SHADOW]: (prop) => prop.css.includes("box-shadow:"),

  [PropertyType.TEXT_COLOR]: (prop) =>
    prop.css.startsWith("color:") &&
    prop.tailwind.startsWith("text-") &&
    !prop.tailwind.includes("/"), // Filter out opacity variants
  [PropertyType.LEFT]: (prop) =>
    prop.css.startsWith("left:") && prop.tailwind.startsWith("left-"),
  [PropertyType.TOP]: (prop) =>
    prop.css.startsWith("top:") && prop.tailwind.startsWith("top-"),
  [PropertyType.RIGHT]: (prop) =>
    prop.css.startsWith("right:") && prop.tailwind.startsWith("right-"),
  [PropertyType.BOTTOM]: (prop) =>
    prop.css.startsWith("bottom:") && prop.tailwind.startsWith("bottom-"),
  [PropertyType.POSITION]: (prop) =>
    prop.css.startsWith("position:") &&
    ["absolute", "relative", "fixed", "sticky", "static"].includes(
      prop.tailwind,
    ),
  [PropertyType.BACKGROUND_IMAGE]: (prop) =>
    prop.css.startsWith("background-image:") &&
    ([
      "bg-none",
      "bg-gradient-to-t",
      "bg-gradient-to-tr",
      "bg-gradient-to-r",
      "bg-gradient-to-br",
      "bg-gradient-to-b",
      "bg-gradient-to-bl",
      "bg-gradient-to-l",
      "bg-gradient-to-tl",
    ].includes(prop.tailwind) ||
      prop.tailwind.startsWith("bg-[url(")),
  [PropertyType.FROM_COLOR]: (prop) =>
    prop.css.includes("--tw-gradient-from:") &&
    prop.tailwind.startsWith("from-") &&
    !prop.tailwind.includes("/"),
  [PropertyType.VIA_COLOR]: (prop) =>
    prop.css.includes("--tw-gradient-stops:") &&
    prop.tailwind.startsWith("via-") &&
    !prop.tailwind.includes("/"),
  [PropertyType.TO_COLOR]: (prop) =>
    prop.css.includes("--tw-gradient-to:") &&
    prop.tailwind.startsWith("to-") &&
    !prop.tailwind.includes("/"),
  [PropertyType.FROM_POSITION]: (prop) =>
    prop.tailwind.startsWith("from-") &&
    (prop.css.startsWith("--tw-gradient-from-position:") ||
      prop.tailwind.includes("[") ||
      !prop.css ||
      prop.css === ""),
  [PropertyType.TO_POSITION]: (prop) =>
    prop.tailwind.startsWith("to-") &&
    (prop.css.startsWith("--tw-gradient-to-position:") ||
      prop.tailwind.includes("[") ||
      !prop.css ||
      prop.css === ""),
  [PropertyType.VIA_POSITION]: (prop) =>
    prop.tailwind.startsWith("via-") &&
    (prop.css.startsWith("--tw-gradient-via-position:") ||
      prop.tailwind.includes("[") ||
      !prop.css ||
      prop.css === ""),
  [PropertyType.TEXT_ALIGN]: (prop) => prop.css.startsWith("text-align:"),
  [PropertyType.DISPLAY]: (prop) => prop.css.startsWith("display:"),
  [PropertyType.FLEX_DIRECTION]: (prop) =>
    prop.css.startsWith("flex-direction:"),
  [PropertyType.FLEX_WRAP]: (prop) => prop.css.startsWith("flex-wrap:"),
  [PropertyType.FLEX_GROW]: (prop) => prop.css.startsWith("flex-grow:"),
  [PropertyType.FLEX_SHRINK]: (prop) => prop.css.startsWith("flex-shrink:"),
};

// Regex patterns for detecting and extracting arbitrary Tailwind classes
export const TAILWIND_CLASS_PATTERNS: {
  [propertyType: string]: [RegExp, (match: RegExpMatchArray) => string | null];
} = {
  [PropertyType.WIDTH]: [/^w-\[?([^\[\]]*)\]?$/, (match) => match[1]],
  [PropertyType.MIN_WIDTH]: [/^min-w-\[?([^\[\]]*)\]?$/, (match) => match[1]],
  [PropertyType.MAX_WIDTH]: [/^max-w-\[?([^\[\]]*)\]?$/, (match) => match[1]],
  [PropertyType.HEIGHT]: [/^h-\[?([^\[\]]*)\]?$/, (match) => match[1]],
  [PropertyType.MIN_HEIGHT]: [/^min-h-\[?([^\[\]]*)\]?$/, (match) => match[1]],
  [PropertyType.MAX_HEIGHT]: [/^max-h-\[?([^\[\]]*)\]?$/, (match) => match[1]],
  [PropertyType.ROTATE]: [/^rotate-\[?([^\[\]]*)\]?$/, (match) => match[1]],
  [PropertyType.BORDER_RADIUS]: [
    /^rounded-\[?([^\[\]]*)\]?$/,
    (match) => match[1],
  ],
  [PropertyType.BORDER_TOP_LEFT_RADIUS]: [
    /^rounded-tl-\[?([^\[\]]*)\]?$/,
    (match) => match[1],
  ],
  [PropertyType.BORDER_TOP_RIGHT_RADIUS]: [
    /^rounded-tr-\[?([^\[\]]*)\]?$/,
    (match) => match[1],
  ],
  [PropertyType.BORDER_BOTTOM_RIGHT_RADIUS]: [
    /^rounded-br-\[?([^\[\]]*)\]?$/,
    (match) => match[1],
  ],
  [PropertyType.BORDER_BOTTOM_LEFT_RADIUS]: [
    /^rounded-bl-\[?([^\[\]]*)\]?$/,
    (match) => match[1],
  ],
  [PropertyType.FONT_WEIGHT]: [/^font-\[?([^\[\]]*)\]?$/, (match) => match[1]],
  [PropertyType.LINE_HEIGHT]: [
    /^leading-\[?([^\[\]]*)\]?$/,
    (match) => match[1],
  ],
  [PropertyType.LETTER_SPACING]: [
    /^tracking-\[?([^\[\]]*)\]?$/,
    (match) => match[1],
  ],
  [PropertyType.PADDING]: [/^p-\[?([^\[\]]*)\]?$/, (match) => match[1]],
  [PropertyType.PADDING_VERTICAL]: [
    /^py-\[?([^\[\]]*)\]?$/,
    (match) => match[1],
  ],
  [PropertyType.PADDING_HORIZONTAL]: [
    /^px-\[?([^\[\]]*)\]?$/,
    (match) => match[1],
  ],
  [PropertyType.PADDING_TOP]: [/^pt-\[?([^\[\]]*)\]?$/, (match) => match[1]],
  [PropertyType.PADDING_BOTTOM]: [/^pb-\[?([^\[\]]*)\]?$/, (match) => match[1]],
  [PropertyType.PADDING_LEFT]: [/^pl-\[?([^\[\]]*)\]?$/, (match) => match[1]],
  [PropertyType.PADDING_RIGHT]: [/^pr-\[?([^\[\]]*)\]?$/, (match) => match[1]],
  [PropertyType.MARGIN]: [/^m-\[?([^\[\]]*)\]?$/, (match) => match[1]],
  [PropertyType.MARGIN_VERTICAL]: [
    /^my-\[?([^\[\]]*)\]?$/,
    (match) => match[1],
  ],
  [PropertyType.MARGIN_HORIZONTAL]: [
    /^mx-\[?([^\[\]]*)\]?$/,
    (match) => match[1],
  ],
  [PropertyType.MARGIN_TOP]: [/^mt-\[?([^\[\]]*)\]?$/, (match) => match[1]],
  [PropertyType.MARGIN_BOTTOM]: [/^mb-\[?([^\[\]]*)\]?$/, (match) => match[1]],
  [PropertyType.MARGIN_LEFT]: [/^ml-\[?([^\[\]]*)\]?$/, (match) => match[1]],
  [PropertyType.MARGIN_RIGHT]: [/^mr-\[?([^\[\]]*)\]?$/, (match) => match[1]],
  [PropertyType.COLUMN_GAP]: [/^gap-x-\[?([^\[\]]*)\]?$/, (match) => match[1]],
  [PropertyType.ROW_GAP]: [/^gap-y-\[?([^\[\]]*)\]?$/, (match) => match[1]],
  [PropertyType.ANIMATION]: [/^animate-\[?([^\[\]]*)\]?$/, (match) => match[1]],
  [PropertyType.BLUR]: [/^blur-\[?([^\[\]]*)\]?$/, (match) => match[1]],
  [PropertyType.OPACITY]: [/^opacity-\[?([^\[\]]*)\]?$/, (match) => match[1]],
  // Captures arbitrary background colors with brackets (e.g. bg-[#ff0000], bg-[blue])
  [PropertyType.BACKGROUND_COLOR]: [/^bg-\[([^\[\]]+)\]$/, (match) => match[1]],
  [PropertyType.JUSTIFY_CONTENT]: [
    /^justify-\[?([^\[\]]*)\]?$/,
    (match) => match[1],
  ],
  [PropertyType.ALIGN_ITEMS]: [/^items-\[?([^\[\]]*)\]?$/, (match) => match[1]],
  [PropertyType.ALIGN_CONTENT]: [
    /^content-\[?([^\[\]]*)\]?$/,
    (match) => match[1],
  ],
  [PropertyType.BORDER_WIDTH]: [
    /^border-\[?([^\[\]]*)\]?$/,
    (match) => {
      const value = match[1];
      // Accept numeric values with optional decimal places and units (e.g., "2", "1.5px", "0.25rem")
      if (/^\d+(\.\d+)?(px|em|rem|%)?$/.test(value)) return value;
      return null;
    },
  ],
  [PropertyType.BORDER_STYLE]: [
    /^border-\[?([^\[\]]*)\]?$/,
    (match) => {
      const value = match[1];
      const borderStyles = [
        "solid",
        "dashed",
        "dotted",
        "double",
        "hidden",
        "none",
      ];
      if (borderStyles.includes(value)) return value;
      return null;
    },
  ],
  [PropertyType.BORDER_COLOR]: [
    /^border-\[([^\[\]]+)\]$/,
    (match) => {
      const value = match[1];
      // Reject numeric values with units (e.g., "2px", "1.5rem") - these are border widths, not colors
      if (/^\d+(\.\d+)?(px|em|rem|%)?$/.test(value)) return null;
      const borderStyles = [
        "solid",
        "dashed",
        "dotted",
        "double",
        "groove",
        "ridge",
        "inset",
        "outset",
        "none",
        "hidden",
      ];
      if (borderStyles.includes(value)) return null;
      return value;
    },
  ],
  [PropertyType.FONT_SIZE]: [
    /^text-\[?([^\[\]]*)\]?$/,
    (match) => {
      const value = match[1];

      // The first character must be a number
      if (Number.isNaN(Number(value[0]))) {
        return null;
      }

      return value;
    },
  ],
  [PropertyType.TEXT_COLOR]: [
    /^text-\[([^\[\]]+)\]$/,
    (match) => {
      const displayValue = match[1];
      if (
        ["left", "center", "right", "justify", "start", "end"].includes(
          displayValue,
        )
      ) {
        return null;
      }

      // If value starts with a number, then it belongs to font-size and not text-color
      if (!Number.isNaN(Number(displayValue[0]))) {
        return null;
      }

      return displayValue;
    },
  ],
  [PropertyType.BACKGROUND_IMAGE]: [
    /^bg-\[([^\[\]]+)\]$/,
    (match) => {
      // Only match actual image URLs or specific background-image values
      const value = match[1];
      if (value.startsWith("url(") || value === "none") {
        return value;
      }
      return null;
    },
  ],
  [PropertyType.LEFT]: [/^left-\[?([^\[\]]*)\]?$/, (match) => match[1]],
  [PropertyType.TOP]: [/^top-\[?([^\[\]]*)\]?$/, (match) => match[1]],
  [PropertyType.RIGHT]: [/^right-\[?([^\[\]]*)\]?$/, (match) => match[1]],
  [PropertyType.BOTTOM]: [/^bottom-\[?([^\[\]]*)\]?$/, (match) => match[1]],
  [PropertyType.POSITION]: [/^position-\[?([^\[\]]*)\]?$/, (match) => match[1]],
  [PropertyType.FROM_COLOR]: [/^from-\[([^\[\]]+)\]$/, (match) => match[1]],
  [PropertyType.VIA_COLOR]: [/^via-\[([^\[\]]+)\]$/, (match) => match[1]],
  [PropertyType.TO_COLOR]: [/^to-\[([^\[\]]+)\]$/, (match) => match[1]],
  [PropertyType.FROM_POSITION]: [/^from-\[([^\[\]]+)\]$/, (match) => match[1]],
  [PropertyType.VIA_POSITION]: [/^via-\[([^\[\]]+)\]$/, (match) => match[1]],
  [PropertyType.TO_POSITION]: [/^to-\[([^\[\]]+)\]$/, (match) => match[1]],
};

export const checkArbitraryTailwindClass = (
  className: string,
  propertyType: PropertyType,
) => {
  if (!TAILWIND_CLASS_PATTERNS[propertyType]) {
    return null;
  }

  const [regex, extractor] = TAILWIND_CLASS_PATTERNS[propertyType];
  if (!regex) {
    return null;
  }

  const match = className.match(regex);
  if (!match) {
    return null;
  }

  const propertyValue = extractor(match);
  if (!propertyValue) {
    return null;
  }

  return {
    propertyValue,
  };
};

/**
 * detect what property type a class matches using TAILWIND_CLASS_PATTERNS
 * Returns the PropertyType if the class matches any pattern, null otherwise
 */
export const detectClassPropertyType = (
  className: string,
): PropertyType | null => {
  // Remove variant prefix to check the base class
  const baseClass = className.includes(":")
    ? className.split(":").slice(1).join(":")
    : className;

  // Check each pattern to see if the class matches
  for (const [propertyType, [regex, extractor]] of Object.entries(
    TAILWIND_CLASS_PATTERNS,
  )) {
    const match = baseClass.match(regex);
    if (match) {
      const value = extractor(match);
      if (value !== null) {
        return propertyType as PropertyType;
      }
    }
  }

  return null;
};

/**
 * Check if a class matches a specific property type using TAILWIND_CLASS_PATTERNS
 */
export const isClassOfPropertyType = (
  className: string,
  propertyType: PropertyType,
): boolean => {
  const detectedType = detectClassPropertyType(className);
  return detectedType === propertyType;
};

/**
 * check if a class is a gradient-related class
 */
export const isGradientClassByPattern = (cls: string): boolean => {
  const baseClass = cls.includes(":") ? cls.split(":").slice(1).join(":") : cls;

  // First check using existing gradient detection logic (handles standard classes like from-red-500)
  if (isGradientClass(baseClass)) {
    return true;
  }

  // Then check if it matches any arbitrary gradient property patterns (handles from-[#ff0000])
  const gradientTypes = [
    PropertyType.BACKGROUND_IMAGE,
    PropertyType.FROM_COLOR,
    PropertyType.TO_COLOR,
    PropertyType.VIA_COLOR,
    PropertyType.FROM_POSITION,
    PropertyType.TO_POSITION,
    PropertyType.VIA_POSITION,
  ];

  for (const propertyType of gradientTypes) {
    if (isClassOfPropertyType(baseClass, propertyType)) {
      return true;
    }
  }

  return false;
};

/**
 * check if a class is a background image class
 */
export const isBackgroundImageClassByPattern = (cls: string): boolean => {
  const baseClass = cls.includes(":") ? cls.split(":").slice(1).join(":") : cls;

  // First check using existing background image detection logic
  if (isBackgroundImageClass(baseClass)) {
    return true;
  }

  // Then check the arbitrary background image pattern
  const patterns = TAILWIND_CLASS_PATTERNS[PropertyType.BACKGROUND_IMAGE];
  if (patterns) {
    const [regex, extractor] = patterns;
    const match = baseClass.match(regex);
    return match ? extractor(match) !== null : false;
  }

  return false;
};

export const getSupportedTailwindClasses = (
  classList: ClassType[],
): ClassTypeWithPropertyType[] => {
  const result: ClassTypeWithPropertyType[] = [];
  for (const classType of classList) {
    const propertyType = Object.keys(tailwindFilterFunctions).find((key) =>
      tailwindFilterFunctions[key as PropertyType](classType),
    );
    if (propertyType) {
      result.push({ ...classType, propertyType: propertyType as PropertyType });
    }
  }
  return result;
};

export const safeExtractValueFromCss = (tailwindClass: ClassType): string => {
  try {
    const firstCss = tailwindClass.css.split("\n")[0];
    let value: string = firstCss.split(":")[1];
    value = value.split(";")[0];

    value = value.trim();
    if (value.endsWith("!important")) {
      value = value.slice(0, -"!important".length);
    }

    value = value.trim();

    if (value.startsWith('"') && value.endsWith('"')) {
      value = value.slice(1, -1);
    }

    value = value.trim();

    return value;
  } catch (error) {
    return tailwindClass.tailwind;
  }
};

export function parseTailwindColorWithOpacity(
  className: string,
): string | null {
  const match = className.match(
    /^(bg|border|text)-([a-z0-9-]+)(?:\/(\d{1,3}))?$/,
  );
  if (!match) return null;
  const prefix = match[1];
  const color = match[2];
  const opacity = match[3] ? Number.parseInt(match[3], 10) : 100;
  const baseClass = `${prefix}-${color}`;
  const css = TAILWIND_LOOKUP[baseClass];
  if (!css) return null;
  // Extract rgb from css string
  const rgbMatch = css.match(/rgb\(([^)]+)\)/);
  if (!rgbMatch) return null;
  const rgb = rgbMatch[1].split(/,| /).filter(Boolean).map(Number);
  const a = Math.round((opacity / 100) * 100) / 100;
  return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${a})`;
}

export const GRADIENT_PROPERTY_TYPES: PropertyType[] = [
  PropertyType.BACKGROUND_IMAGE,
  PropertyType.FROM_COLOR,
  PropertyType.TO_COLOR,
  PropertyType.VIA_COLOR,
  PropertyType.FROM_POSITION,
  PropertyType.VIA_POSITION,
  PropertyType.TO_POSITION,
];

/**
 * Converts Tailwind classes using CSS variables (e.g., bg-[var(--border)])
 * to the most likely Tailwind class (e.g., bg-border), for any property.
 *
 * @param option An object with `tailwind` and `css` string properties.
 * @returns A new object with the converted Tailwind class if possible.
 */
export function convertOptionCssVariableToTailwindClass<
  T extends { tailwind: string; css: string },
>(option: T): T {
  // Regex to match Tailwind arbitrary value classes using CSS variables, e.g., bg-[var(--border)]
  // Captures: property (bg), variable name (border)
  const regex = /^(\w+)-\[var\(--([\w-]+)\)\]$/;
  const match = option.tailwind.match(regex);

  if (match) {
    const property = match[1]; // e.g., bg
    const variable = match[2]; // e.g., border

    // Convert to Tailwind class: bg-border
    return {
      ...option,
      tailwind: `${property}-${variable}`,
    };
  }

  // If not a match, return as-is
  return option;
}

/**
 * Utility functions for checking Tailwind class patterns
 */

/**
 * Check if a class is a background-related class (solid colors, gradients, or images)
 */
export const isBackgroundClass = (cls: string): boolean => {
  return cls.startsWith("bg-") || cls.startsWith("dark:bg-");
};

/**
 * Check if a class is a solid background color class
 * Excludes gradient and image classes
 */
export const isSolidBackgroundClass = (cls: string): boolean => {
  const baseClass = cls.startsWith("dark:") ? cls.replace("dark:", "") : cls;
  return (
    baseClass.startsWith("bg-") &&
    !baseClass.startsWith("bg-gradient-") &&
    !baseClass.startsWith("bg-[url(")
  );
};

/**
 * Check if a class is a gradient direction class
 */
export const isGradientDirectionClass = (cls: string): boolean => {
  return cls.startsWith("bg-gradient-");
};

/**
 * Check if a class is a gradient color stop class
 */
export const isGradientColorClass = (cls: string): boolean => {
  return (
    cls.startsWith("from-") ||
    cls.startsWith("to-") ||
    cls.startsWith("via-") ||
    cls.startsWith("dark:from-") ||
    cls.startsWith("dark:to-") ||
    cls.startsWith("dark:via-")
  );
};

/**
 * Check if a class is a gradient position class (includes percentage)
 */
export const isGradientPositionClass = (cls: string): boolean => {
  return (
    (cls.startsWith("from-") ||
      cls.startsWith("to-") ||
      cls.startsWith("via-")) &&
    cls.includes("%")
  );
};

/**
 * Check if a class is any gradient-related class
 */
export const isGradientClass = (cls: string): boolean => {
  return isGradientDirectionClass(cls) || isGradientColorClass(cls);
};

/**
 * Check if a class is a background image class
 */
export const isBackgroundImageClass = (cls: string): boolean => {
  // Handle both quoted and unquoted URL formats:
  // bg-[url('...')] (old format with quotes)
  // bg-[url(...)] (new format without quotes)
  // Also handle variants like hover:bg-[url(...)] or dark:bg-[url(...)]

  // Can't use split(':') because URLs contain colons
  // Instead, look for the bg-[url( pattern anywhere in the string
  return cls.includes("bg-[url(");
};

/**
 * Check if a class is a custom bracket color (e.g., bg-[#ff0000])
 */
export const isCustomColorClass = (cls: string): boolean => {
  return (
    cls.startsWith("bg-[") && !cls.startsWith("bg-[url(") && cls.endsWith("]")
  );
};

/**
 * Check if any class in an array matches a background-related pattern
 */
export const hasBackgroundClasses = (classes: string[]): boolean => {
  return classes.some(
    (cls) => isBackgroundClass(cls) || isGradientColorClass(cls),
  );
};

/**
 * Check if any class in an array is a gradient class
 */
export const hasGradientClasses = (classes: string[]): boolean => {
  return classes.some(isGradientClass);
};

/**
 * Check if any class in an array is a background image class
 */
export const hasBackgroundImageClasses = (classes: string[]): boolean => {
  return classes.some(isBackgroundImageClass);
};

/**
 * Extract the base class without dark: prefix
 */
export const getBaseClass = (cls: string): string => {
  return cls.startsWith("dark:") ? cls.replace("dark:", "") : cls;
};

/**
 * Extract custom color value from bracket notation
 * Returns null if not a custom color class
 */
export const extractCustomColorFromClass = (
  tailwindClass: string,
): string | null => {
  const baseClass = getBaseClass(tailwindClass);
  if (
    baseClass.startsWith("bg-[") &&
    baseClass.endsWith("]") &&
    !baseClass.startsWith("bg-[url(")
  ) {
    const customColor = baseClass.slice(4, -1); // Remove bg-[ and ]
    if (
      customColor.startsWith("#") ||
      customColor.startsWith("rgb") ||
      customColor.startsWith("hsl")
    ) {
      return customColor;
    }
  }
  return null;
};

/**
 * Convert dark variant class name to its corresponding light class for token lookup
 * Handles the naming convention: dark:bg-dark-1 -> bg-1
 * This allows looking up the same base token ('1') in both light and dark color groups
 */
export const getDarkTokenLookupClass = (darkClass: string): string => {
  // Remove dark: prefix first
  const baseClass = darkClass.startsWith("dark:")
    ? darkClass.replace("dark:", "")
    : darkClass;

  // Convert bg-dark-X to bg-X for token lookup
  if (baseClass.startsWith("bg-dark-")) {
    return baseClass.replace("bg-dark-", "bg-");
  }

  // Handle other prefixes if needed
  if (baseClass.startsWith("text-dark-")) {
    return baseClass.replace("text-dark-", "text-");
  }

  if (baseClass.startsWith("border-dark-")) {
    return baseClass.replace("border-dark-", "border-");
  }

  return baseClass;
};

// Helper function to find the opposite variant (light<->dark) of a color
// only pass in the light variant, and it will return the dark variant
export const findDarkVariant = (
  currentOption: ComboBoxOptionType,
): ComboBoxOptionType | null => {
  // Extract the color name from the tailwind class
  // e.g., "bg-accent-foreground" -> "accent-foreground", "text-primary-default" -> "primary-default"
  const colorMatch = currentOption.tailwind.match(
    /(?:bg-|text-|border-|ring-)(.+)/,
  );
  if (!colorMatch) return null;

  const tailwindColorName = colorMatch[1];
  const prefix =
    currentOption.tailwind.match(/^(bg-|text-|border-|ring-)/)?.[1] || "bg-";

  // Currently in light mode (showing regular colors), create dark mode variant
  // Add 'dark-' prefix to the color name and use dark: variant prefix
  const darkColorName = `dark-${tailwindColorName}`;
  return {
    tailwind: `dark:${prefix}${darkColorName}`,
    css: currentOption.css,
    displayValue: currentOption.displayValue,
  };
};
const FILTERED_PROPERTIES = getSupportedTailwindClasses(PROPERTIES);

// TODO(petergok): create lookup in store
const lookup_build = {};
for (const prop of FILTERED_PROPERTIES) {
  lookup_build[prop.tailwind] = prop.css;
}
export const TAILWIND_LOOKUP = lookup_build;
export const TAILWIND_PROPERTIES = FILTERED_PROPERTIES.sort((a, b) =>
  a.tailwind.localeCompare(b.tailwind),
);

export const FILTER_FUNCTION_LIST = Object.values(tailwindFilterFunctions);

/**
 * Resolves a background color from Tailwind classes using provided color groups and ungrouped colors.
 */
export const resolveDesignTokenColor = (
  classes: string[],
  lightColorGroups: ColorGroup[],
  lightColors: ColorToken[],
  darkColorGroups: ColorGroup[],
  darkColors: ColorToken[],
  isCanvasDarkMode = false,
): string | null => {
  const bgClasses = classes.filter(
    (cls) =>
      (cls.startsWith("bg-") || cls.includes(":bg-")) &&
      !cls.includes("gradient"),
  );

  if (bgClasses.length === 0) {
    return null;
  }

  // Choose the appropriate color sets based on canvas dark mode
  const activeColorGroups = isCanvasDarkMode
    ? darkColorGroups
    : lightColorGroups;
  const activeColors = isCanvasDarkMode ? darkColors : lightColors;

  // Check for both light and dark variants
  const lightBgClass = bgClasses.find((cls) => !cls.includes("dark:"));
  const darkBgClass = bgClasses.find((cls) => cls.includes("dark:"));

  // Use the appropriate class based on canvas dark mode
  const targetClass = isCanvasDarkMode
    ? darkBgClass || lightBgClass
    : lightBgClass;

  if (!targetClass) {
    return null;
  }

  // Extract color path from class
  let colorPath: string;
  if (targetClass.includes("dark:bg-dark-")) {
    // Dark mode class like 'dark:bg-dark-primary' or 'dark:bg-dark-primary-foreground'
    const extractedPath = targetClass.replace("dark:bg-dark-", "");
    // For dark colors, we need to add back the -default suffix for database lookup
    // if the extracted path doesn't contain additional suffixes
    colorPath = extractedPath.includes("-")
      ? extractedPath
      : `${extractedPath}-default`;
  } else if (targetClass.includes("dark:bg-")) {
    // Dark mode class like 'dark:bg-primary-default' (shouldn't happen with current system)
    colorPath = targetClass.replace("dark:bg-", "");
  } else {
    // Light mode class like 'bg-primary-default'
    colorPath = targetClass.replace("bg-", "");
  }

  const ungroupedColor = activeColors.find(
    (color) => color.tailwindClass === colorPath,
  );

  if (ungroupedColor) {
    // If we have a resolved color value, use it (this comes from CSS custom properties resolution)
    if (ungroupedColor.color) {
      return ungroupedColor.color;
    }

    // Check if the value is a CSS custom property (contains 'var(')
    if (ungroupedColor.value.includes("var(")) {
      // Return the color property (which may be resolved or still a CSS variable)
      return ungroupedColor.color;
    }

    const result = ungroupedColor.value.startsWith("#")
      ? ungroupedColor.value
      : `#${ungroupedColor.value}`;

    return result;
  }

  // Look in grouped colors - check by tailwindClass
  for (const group of activeColorGroups) {
    // Look for color with matching tailwindClass
    const colorByTailwindClass = group.colors.find(
      (c) => c.tailwindClass === colorPath,
    );

    if (colorByTailwindClass) {
      // If we have a resolved color value, use it (this comes from CSS custom properties resolution)
      if (colorByTailwindClass.color) {
        return colorByTailwindClass.color;
      }

      // Check if the value is a CSS custom property (contains 'var(')
      if (colorByTailwindClass.value.includes("var(")) {
        return null; // Let CSS computed value handle it
      }

      const result = colorByTailwindClass.value.startsWith("#")
        ? colorByTailwindClass.value
        : `#${colorByTailwindClass.value}`;

      return result;
    }

    // Fallback: Try exact match by name (for cases like 'primary-default')
    const exactColor = group.colors.find(
      (c) => `${group.name}-${c.name}` === colorPath,
    );

    if (exactColor) {
      // If we have a resolved color value, use it (this comes from CSS custom properties resolution)
      if (exactColor.color) {
        return exactColor.color;
      }

      // Check if the value is a CSS custom property (contains 'var(')
      if (exactColor.value.includes("var(")) {
        // Return the color property (which may be resolved or still a CSS variable)
        return exactColor.color;
      }

      const result = exactColor.value.startsWith("#")
        ? exactColor.value
        : `#${exactColor.value}`;

      return result;
    }

    // Fallback: Try splitting the path for nested matches
    if (colorPath.includes("-")) {
      const parts = colorPath.split("-");
      const groupName = parts[0];
      const colorName = parts.slice(1).join("-"); // Handle multi-part color names

      if (group.name === groupName) {
        const color = group.colors.find((c) => c.name === colorName);

        if (color) {
          // Check if the value is a CSS custom property (contains 'var(')
          if (color.value.includes("var(")) {
            // Return the color property (which may be resolved or still a CSS variable)
            return color.color;
          }
          const result = color.value.startsWith("#")
            ? color.value
            : `#${color.value}`;

          return result;
        }
      }
    } else {
      // Handle case where colorPath matches group name and we want the 'default' color
      if (group.name === colorPath) {
        const defaultColor = group.colors.find((c) => c.name === "default");

        if (defaultColor) {
          // Check if the value is a CSS custom property (contains 'var(')
          if (defaultColor.value.includes("var(")) {
            // Return the color property (which may be resolved or still a CSS variable)
            return defaultColor.color;
          }
          const result = defaultColor.value.startsWith("#")
            ? defaultColor.value
            : `#${defaultColor.value}`;

          return result;
        }
      }
    }
  }

  return null;
};
